import { useState } from "react";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { PageHeader, LoadingState, EmptyState, ErrorState } from "@/components/features/states";
import { TrustBadge } from "@/components/features/badges";
import { formatRelativeTime } from "@/lib/format";
import { Users, Search, Star, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

export function AdminUsers() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => api.admin.users(),
  });
  const users = data?.data?.users ?? [];

  const filtered = users.filter((u: any) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return u.name.toLowerCase().includes(s) || u.email.toLowerCase().includes(s);
  });

  if (isLoading) return <LoadingState />;
  if (error || !data?.ok) return <ErrorState message={data?.error || t("admin.users.loadFailed")} onRetry={refetch} />;

  return (
    <div className="space-y-4">
      <PageHeader title={t("admin.users.title")} subtitle={t("admin.users.subtitle", { count: users.length })} />

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm"
          placeholder={t("admin.users.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={<Users className="h-8 w-8" />} title={t("admin.users.noUsersFound")} />
      ) : (
        <div className="space-y-2">
          {filtered.map((u: any) => (
            <Card key={u.id}>
              <CardContent className="flex items-center gap-3 p-3.5">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-primary/10 text-xs font-bold text-primary">
                    {u.name.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-semibold">{u.name}</p>
                    {u.isAdmin && <Badge variant="secondary" className="text-[10px]">{t("admin.users.adminBadge")}</Badge>}
                  </div>
                  <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                      {u.rating?.toFixed(1)} ({u.ratingCount})
                    </span>
                    <span>·</span>
                    <span className="capitalize">{u.activeRole}</span>
                    <span>·</span>
                    <TrustBadge status={u.trust?.status || u.trustStatus || "none"} />
                    <span>·</span>
                    <span>{t("admin.users.joinedAgo", { time: formatRelativeTime(u.createdAt) })}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
